import { emitTicketMutationEvent } from "@jugl-web/domain-resources/tickets/events/ticketMutation";
import {
  apiErrorHasCode,
  parseApiError,
  UpdateTicketPayload,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { HorizontalScrollableContainer, Tab } from "@jugl-web/ui-kit";
import { useToast } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, useEffect, useMemo, useState } from "react";
import { TicketSidebarHeader } from "../../../TicketSidebarHeader";
import { TicketDetailsTab, TicketDetailsTabId } from "../../../../types";
import { CustomerTabContent } from "../CustomerTabContent";
import { DetailsTabContent } from "../DetailsTabContent";
import { HistoryTabContent } from "../HistoryTabContent";
import { TeamNotesTabContent } from "../TeamNotesTabContent";
import { TicketDetailsLoadingErrorState } from "./TicketDetailsLoadingErrorState";
import { TicketDetailsLoadingState } from "./TicketDetailsLoadingState";
import { TicketDetailsNotFoundState } from "./TicketDetailsNotFoundState";

interface TicketDetailsSidebarContentProps {
  ticketId: string;
  /* If the ticket is nested, this is the ID of the root ticket */
  rootTicketId?: string;
  initialTabId?: TicketDetailsTabId;
  onClose: () => void;
  onTicketLookup: (ticketId: string) => void;
  onGoBack?: () => void;
}

export const TicketDetailsSidebarContent: FC<
  TicketDetailsSidebarContentProps
> = ({
  ticketId,
  rootTicketId,
  initialTabId = "details",
  onClose,
  onTicketLookup,
  onGoBack,
}) => {
  const { entityId } = useEntitySelectedProvider();

  const [activeTabId, setActiveTabId] =
    useState<TicketDetailsTabId>(initialTabId);

  const { ticketsApi } = useRestApiProvider();
  const [updateTicketMutation] = ticketsApi.useUpdateTicketMutation();
  const [deleteTicketMutation] = ticketsApi.useDeleteTicketMutation();

  const { toast } = useToast();

  const {
    data: ticket,
    isLoading,
    isError,
    error,
    refetch,
  } = ticketsApi.useGetTicketByIdQuery(
    { entityId, ticketId, meta: { silent: true } },
    { refetchOnMountOrArgChange: true }
  );

  const [teamNotesTotalEntries, setTeamNotesTotalEntries] = useState<number>(
    ticket?.stats.comments ?? 0
  );

  const tabs = useMemo<TicketDetailsTab[]>(
    () => [
      { id: "details", title: "Details" },
      { id: "customer", title: "Customer" },
      { id: "team-notes", title: "Team Notes", count: teamNotesTotalEntries },
      { id: "history", title: "History", count: ticket?.stats.history },
    ],
    [teamNotesTotalEntries, ticket?.stats.history]
  );

  const handleUpdateTicket = async (data: UpdateTicketPayload) => {
    try {
      const updatedTicket = await updateTicketMutation({
        entityId,
        ticketId,
        data,
        meta: {
          getErrorMessage: () => "Failed to update ticket",
        },
      }).unwrap();

      emitTicketMutationEvent({
        type: "updated",
        ticket: updatedTicket,
        isLocal: true,
      });
      return updatedTicket;
    } catch {
      return undefined;
    }
  };

  const handleResolveTicket = async () => {
    const updatedTicket = await handleUpdateTicket({ status: "resolved" });

    if (updatedTicket) {
      toast("Ticket has been resolved");
    }
  };

  const handleDeleteTicket = async () => {
    try {
      await deleteTicketMutation({
        entityId,
        ticketId,
        meta: {
          successMessage: "Ticket has been deleted",
          getErrorMessage: () => "Failed to delete ticket",
        },
      }).unwrap();

      emitTicketMutationEvent({ type: "deleted", ticketId });
      onClose();
    } catch {
      // Error toast is handled by `meta` above
    }
  };

  // Update local state of team notes count once ticket is loaded
  useEffect(() => {
    if (ticket) {
      setTeamNotesTotalEntries(ticket.stats.comments);
    }
  }, [ticket]);

  const isTicketNotFoundError =
    isError && apiErrorHasCode(parseApiError(error), "ticket_not_found");

  const isTicketDeletable = !!ticket;
  const isTicketResolvable = !!ticket && ticket.status !== "resolved";

  if (isLoading) {
    return <TicketDetailsLoadingState />;
  }

  if (isTicketNotFoundError) {
    return <TicketDetailsNotFoundState onClose={onClose} />;
  }

  if (isError || !ticket) {
    return <TicketDetailsLoadingErrorState onRetry={() => refetch()} />;
  }

  return (
    <div className="flex h-full flex-col">
      <TicketSidebarHeader
        title={ticket.title}
        ticketMetadata={{
          ticketUniqueId: ticket.unique_id,
          ticketOwnerName: ticket.contact.name,
        }}
        onClose={onClose}
        onGoBack={onGoBack}
        onResolveTicket={isTicketResolvable ? handleResolveTicket : undefined}
        onDeleteTicket={isTicketDeletable ? handleDeleteTicket : undefined}
      />
      <div className="flex min-h-0 flex-1 flex-col">
        <HorizontalScrollableContainer wrapperClassName="border-grey-200 shrink-0 border-b border-solid">
          <div className="flex h-13 items-end px-6">
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                title={tab.title}
                isSelected={activeTabId === tab.id}
                className="w-auto"
                labelClassName="text-sm"
                count={tab.count}
                onClick={() => setActiveTabId(tab.id)}
              />
            ))}
          </div>
        </HorizontalScrollableContainer>

        <div
          key={activeTabId}
          className={
            activeTabId === "team-notes" || activeTabId === "history"
              ? "flex min-h-0 flex-1 animate-fade-in flex-col overflow-hidden"
              : "jugl__custom-scrollbar min-h-0 flex-1 animate-fade-in overflow-y-auto"
          }
        >
          {activeTabId === "details" && <DetailsTabContent ticket={ticket} />}
          {activeTabId === "customer" && (
            <CustomerTabContent
              ticket={ticket}
              rootTicketId={rootTicketId}
              onTicketLookup={onTicketLookup}
            />
          )}
          {activeTabId === "team-notes" && (
            <TeamNotesTabContent
              ticket={ticket}
              onTotalEntriesChange={setTeamNotesTotalEntries}
            />
          )}
          {activeTabId === "history" && <HistoryTabContent ticket={ticket} />}
        </div>
      </div>
    </div>
  );
};
